import {
  AUTHORIZED_DOMAINS,
  PAYMENT_POPUP_FEATURES,
  WINDOW_CLOSE_CHECK_INTERVAL_MS,
  WINDOW_OPEN_TIMEOUT_MS,
  WINDOW_POLL_INTERVAL_MS,
} from '@/utils/paymentConstants'
import { handlePaymentError } from '@/utils/paymentErrorHandler'
import { isPopupBlocked } from '@/utils/paymentUtils'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hook for managing external payment windows (popups, iframes).
 *
 * Supported Use Cases:
 * 1. Open payment popup with blocker detection
 * 2. Monitor window for premature close
 * 3. Receive postMessage from popup (PAYMENT_CONFIRM_RESULT, PAYMENT_REDIRECT)
 * 4. Origin validation for security
 * 5. Timeout detection for window open
 *
 * State Flow:
 *   isWindowActive: false → openWindow() → true
 *   isWindowActive: true → postMessage received → false
 *   isWindowActive: true → window closed prematurely → false + onWindowClosed
 *   isWindowActive: true → stopMonitoring() → false
 */
export const useWindowManager = ({
  onError,
  onWindowClosed,
  onRedirect,
  onConfirmResult,
} = {}) => {
  const [isWindowActive, setIsWindowActive] = useState(false)
  const windowRef = useRef(null)
  const closeMonitorIntervalRef = useRef(null)
  const redirectReceivedRef = useRef(false)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeMonitorIntervalRef.current) {
        clearInterval(closeMonitorIntervalRef.current)
        closeMonitorIntervalRef.current = null
      }
    }
  }, [])

  // Listen for postMessage from popup windows
  useEffect(() => {
    const handleMessage = (event) => {
      const safeOrigin =
        AUTHORIZED_DOMAINS.paypal.includes(event.origin) ||
        event.origin === window.location.origin ||
        AUTHORIZED_DOMAINS.braintree.includes(event.origin) ||
        AUTHORIZED_DOMAINS.poste.includes(event.origin) ||
        AUTHORIZED_DOMAINS.cookiebot.includes(event.origin)

      if (!safeOrigin) {
        console.warn(
          '[WindowManager] Ignoring message from untrusted origin:',
          event.origin
        )
        return
      }

      // Legacy redirect message
      if (event.data?.type === 'PAYMENT_REDIRECT') {
        redirectReceivedRef.current = true
        if (closeMonitorIntervalRef.current) {
          clearInterval(closeMonitorIntervalRef.current)
          closeMonitorIntervalRef.current = null
        }
        setIsWindowActive(false)
        windowRef.current = null
        if (onRedirect) onRedirect(event.data)
      }

      // New confirm result message
      if (event.data?.type === 'PAYMENT_CONFIRM_RESULT') {
        redirectReceivedRef.current = true
        if (closeMonitorIntervalRef.current) {
          clearInterval(closeMonitorIntervalRef.current)
          closeMonitorIntervalRef.current = null
        }
        setIsWindowActive(false)
        windowRef.current = null
        if (onConfirmResult) onConfirmResult(event.data)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onRedirect, onConfirmResult])

  const stopWindowMonitoring = useCallback(() => {
    if (closeMonitorIntervalRef.current) {
      clearInterval(closeMonitorIntervalRef.current)
      closeMonitorIntervalRef.current = null
    }
    setIsWindowActive(false)
    windowRef.current = null
  }, [])

  const startWindowMonitoring = useCallback(
    (paymentWindow) => {
      if (!paymentWindow) return

      windowRef.current = paymentWindow
      setIsWindowActive(true)
      redirectReceivedRef.current = false

      if (closeMonitorIntervalRef.current) {
        clearInterval(closeMonitorIntervalRef.current)
      }

      closeMonitorIntervalRef.current = setInterval(() => {
        if (windowRef.current?.closed) {
          // If we received a redirect/confirm message, closure is expected
          if (redirectReceivedRef.current) {
            stopWindowMonitoring()
            return
          }

          // Premature close - user closed window without completing payment
          stopWindowMonitoring()
          const error = new Error('WINDOW_CLOSED_PREMATURELY')
          error.message =
            'La finestra di pagamento è stata chiusa. Clicca su "Ritenta" per procedere con il pagamento.'
          handlePaymentError(error)
          if (onWindowClosed) onWindowClosed(error)
        }
      }, WINDOW_CLOSE_CHECK_INTERVAL_MS)
    },
    [stopWindowMonitoring, onWindowClosed]
  )

  const detectWindowOpen = useCallback((paymentWindow) => {
    return new Promise((resolve) => {
      const startTime = Date.now()
      const checkInterval = setInterval(() => {
        const elapsed = Date.now() - startTime

        // Window was closed during detection phase
        if (paymentWindow?.closed) {
          clearInterval(checkInterval)
          resolve(false)
          return
        }

        // Timeout reached
        if (elapsed >= WINDOW_OPEN_TIMEOUT_MS) {
          clearInterval(checkInterval)
          resolve(false)
          return
        }

        // Window is open and accessible
        try {
          if (paymentWindow && !paymentWindow.closed) {
            clearInterval(checkInterval)
            resolve(true)
          }
        } catch (e) {
          // Cross-origin access error - window is open but not accessible (expected)
          clearInterval(checkInterval)
          resolve(true)
        }
      }, WINDOW_POLL_INTERVAL_MS)
    })
  }, [])

  const openPaymentWindow = useCallback(
    async (url) => {
      if (!url) return { success: false, error: 'NO_URL' }

      try {
        // Reset state for new window
        redirectReceivedRef.current = false
        setIsWindowActive(true)

        const paymentWindow = window.open(url, '_blank', PAYMENT_POPUP_FEATURES)

        // Check for popup blocker
        if (isPopupBlocked(paymentWindow)) {
          const errorMessage =
            'Il popup di pagamento è stato bloccato. Abilita i popup per questo sito e riprova.'
          setIsWindowActive(false)
          handlePaymentError({ message: errorMessage })
          if (onError) onError(new Error('POPUP_BLOCKED'))
          return {
            success: false,
            error: 'POPUP_BLOCKED',
            message: errorMessage,
            windowDetected: false,
          }
        }

        windowRef.current = paymentWindow

        // Wait to detect if window actually opened
        const windowDetected = await detectWindowOpen(paymentWindow)

        if (!windowDetected) {
          const wasClosedPrematurely = paymentWindow?.closed
          const errorMessage = wasClosedPrematurely
            ? 'La finestra di pagamento è stata chiusa. Clicca su "Ritenta" per procedere con il pagamento.'
            : 'La finestra di pagamento non si è aperta. Verifica che i popup siano abilitati e riprova.'

          setIsWindowActive(false)
          windowRef.current = null
          handlePaymentError({ message: errorMessage })

          const errorCode = wasClosedPrematurely
            ? 'WINDOW_CLOSED_PREMATURELY'
            : 'WINDOW_NOT_OPENED'
          if (onError) onError(new Error(errorCode))
          if (wasClosedPrematurely && onWindowClosed)
            onWindowClosed(new Error('WINDOW_CLOSED_PREMATURELY'))

          return {
            success: false,
            error: errorCode,
            message: errorMessage,
            windowDetected: false,
          }
        }

        return { success: true, window: paymentWindow, windowDetected: true }
      } catch (error) {
        setIsWindowActive(false)
        windowRef.current = null
        handlePaymentError(error)
        if (onError) onError(error)
        return {
          success: false,
          error: error.message || 'WINDOW_OPEN_ERROR',
          windowDetected: false,
        }
      }
    },
    [onError, onWindowClosed, detectWindowOpen]
  )

  return {
    // Primary API
    openWindow: openPaymentWindow,
    startMonitoring: startWindowMonitoring,
    stopMonitoring: stopWindowMonitoring,
    isWindowActive,

    // Legacy aliases for backward compatibility
    openPaymentWindow,
    startWindowMonitoring,
    stopWindowMonitoring,
  }
}

export default useWindowManager
